
import React from 'react';
import { useLanguage } from './LanguageContext';
import { CONFIG, TRANSLATIONS } from '../constants';
import { ChevronRight, Shield, CheckCircle2 } from 'lucide-react';

const Hero: React.FC = () => {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  return (
    <section className="relative min-h-[90vh] bg-[#0a0a0a] flex items-center overflow-hidden">
      {/* Background Subtle Elements */}
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-2 gap-12 items-center relative z-10 pt-20 lg:pt-0">

        {/* Text Content */}
        <div className="text-left animate-slide-up">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-[1px] w-8 bg-[#c5a059]"></span>
            <span className="text-[#c5a059] text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase rounded-full">
              Sophisticated Advocacy
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold mb-8 leading-[1] tracking-tight text-white">
            {t.heroTitle.split(' ').map((word: string, i: number) => (
              <span key={i} className={`inline-block mr-3 md:mr-5 ${word.match(/MD|DC|VA/) ? 'text-[#c5a059]' : ''}`}>{word}</span>
            ))}
          </h1>

          <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-xl leading-relaxed font-light opacity-90 border-l border-[#c5a059]/30 pl-6">
            {t.heroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <a
              href="#contact"
              className="group bg-[#c5a059] hover:bg-[#b08b49] text-white px-10 py-5 font-bold text-xs md:text-sm tracking-[0.2em] transition-all duration-500 flex items-center gap-3 shadow-[0_20px_50px_rgba(197,160,89,0.3)] hover:-translate-y-1"
            >
              {t.heroCtaPrimary}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <div className="flex items-center gap-4 text-white/60 text-[10px] font-bold tracking-widest uppercase py-5">
              <Shield className="w-5 h-5 text-[#c5a059]" />
              Licensed in MD • DC • VA
            </div>
          </div>
        </div>

        {/* Lawyer Image Content */}
        <div className="relative group animate-fade-in delay-200">
          <div className="absolute -inset-4 bg-[#c5a059]/10 rounded-sm blur-2xl group-hover:bg-[#c5a059]/20 transition-all duration-700"></div>
          <div className="relative bg-[#111] border border-white/5 p-2 rounded-sm shadow-2xl overflow-hidden cursor-default">

            {/* The Image */}
            <img
              src={CONFIG.attorneyPhoto}
              alt="Lead Attorney"
              className="w-full h-auto object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
              style={{ maxHeight: '700px' }}
            />

            {/* Overlay Info Box - "Dedicated Legal Counsel" Effect */}
            <div className="absolute bottom-10 left-0 right-0 px-8 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
              <div className="bg-[#0a0a0a]/90 backdrop-blur-md p-6 border-l-4 border-[#c5a059] shadow-2xl flex justify-between items-center">
                <div className="flex flex-col">
                  <p className="text-[#c5a059] text-[10px] font-bold tracking-[0.3em] uppercase mb-1">Principal Attorney</p>
                  <h3 className="text-white text-2xl font-serif font-bold">Akil Tirana, Esq.</h3>
                  <div className="flex gap-4 mt-2">
                    <span className="text-[9px] text-white/40 font-bold uppercase tracking-widest flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5 text-[#c5a059]" /> MD</span>
                    <span className="text-[9px] text-white/40 font-bold uppercase tracking-widest flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5 text-[#c5a059]" /> DC</span>
                    <span className="text-[9px] text-white/40 font-bold uppercase tracking-widest flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5 text-[#c5a059]" /> VA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-12 hidden lg:flex flex-col items-center gap-3 opacity-40">
        <div className="w-[1px] h-12 bg-gradient-to-t from-[#c5a059] to-transparent"></div>
        <span className="text-[9px] tracking-[0.4em] uppercase font-bold text-white [writing-mode:vertical-lr]">Discover</span>
      </div>
    </section>
  );
};

export default Hero;
